import { useCallback, useState } from 'react';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/components/ui/Button';
import { QariStepper, type StepperStep } from '@/components/qari/QariStepper';
import { AutosaveIndicator } from '@/components/qari/AutosaveIndicator';
import { StepPersonal } from '@/components/qari/steps/StepPersonal';
import { StepQualifications } from '@/components/qari/steps/StepQualifications';
import { StepDocuments } from '@/components/qari/steps/StepDocuments';
import { StepReview } from '@/components/qari/steps/StepReview';
import { useQariMutations } from '@/hooks/useQariProfile';
import { useDebouncedAutosave } from '@/hooks/useDebouncedAutosave';
import { upsertQariDraft } from '@/lib/queries/qari';
import { useQueryClient } from '@tanstack/react-query';
import type { QariProfile, QariProfileDraft } from '@/types/qari';

type Props = {
  profile: QariProfile;
  /** Called after successful submit (status → pending_review). */
  onSubmitted?: () => void;
};

const STEPS: StepperStep[] = [
  { id: 'personal', label: 'Personal' },
  { id: 'qualifications', label: 'Qualifications' },
  { id: 'documents', label: 'Documents' },
  { id: 'review', label: 'Review' },
];

function toDraft(profile: QariProfile): QariProfileDraft {
  return {
    full_name: profile.full_name,
    bio: profile.bio,
    country: profile.country,
    city: profile.city,
    languages: profile.languages,
    years_experience: profile.years_experience,
    qiraat: profile.qiraat,
    specializations: profile.specializations,
    hifz_status: profile.hifz_status,
    contact_email: profile.contact_email,
    contact_phone: profile.contact_phone,
  };
}

/**
 * OnboardingWizard
 *
 * 4-step flow over a single qari_profiles row.
 *
 * Editing model:
 *  - All inputs bind to `form.value` (managed by useDebouncedAutosave).
 *  - Typing only updates local state — no Supabase request per keystroke.
 *  - Saves are batched after a 1 s debounce, on step navigation, on the
 *    "Save Draft" button, and on Submit. Overlapping save requests are
 *    serialized by the hook.
 *  - We deliberately do NOT refetch the profile after a save; the hook's
 *    save callback writes the returned row into the React Query cache
 *    via `setQueryData`, so the dashboard sees the latest data without a
 *    network roundtrip and inputs never remount.
 */
export function OnboardingWizard({ profile, onSubmitted }: Props) {
  const [step, setStep] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const session = useSession();
  const userId = session.data?.user.id ?? '';
  const qc = useQueryClient();
  const { submitForReview } = useQariMutations();

  // The autosave save callback. We call upsertQariDraft directly (rather
  // than going through the mutation in useQariMutations) so the hook
  // can own concurrency and status. After success we write the saved
  // row into the cache so consumers stay in sync without a refetch.
  const save = useCallback(
    async (next: QariProfileDraft) => {
      if (!userId) throw new Error('Sign in required.');
      const saved = await upsertQariDraft(userId, next);
      qc.setQueryData(['qari-profile', userId], saved);
      return saved;
    },
    [userId, qc],
  );

  const form = useDebouncedAutosave<QariProfileDraft>({
    initialValue: toDraft(profile),
    save,
    debounceMs: 1000,
  });

  const canSubmit =
    (form.value.full_name?.trim().length ?? 0) > 0 &&
    (form.value.bio?.trim().length ?? 0) > 0;

  async function goToStep(i: number) {
    await form.flush();
    setStep(i);
  }

  async function handleSubmit() {
    setSubmitError(null);
    try {
      await form.flush();
      await submitForReview.mutateAsync();
      onSubmitted?.();
    } catch (e) {
      setSubmitError(
        e instanceof Error ? e.message : 'Could not submit. Try again.',
      );
    }
  }

  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  return (
    <div>
      <QariStepper
        steps={STEPS}
        current={step}
        onJump={goToStep}
      />

      <div className="rounded-2xl border border-ink-200/60 dark:border-ink-800/60 bg-white dark:bg-ink-950/40 p-6 sm:p-8">
        {step === 0 && (
          <StepPersonal value={form.value} onChange={form.patch} />
        )}
        {step === 1 && (
          <StepQualifications value={form.value} onChange={form.patch} />
        )}
        {step === 2 && <StepDocuments />}
        {step === 3 && <StepReview profile={profile} />}
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <Button
          variant="ghost"
          onClick={() => goToStep(Math.max(0, step - 1))}
          disabled={isFirst}
        >
          Back
        </Button>

        <Button
          variant="ghost"
          onClick={() => {
            void form.flush();
          }}
          disabled={!form.isDirty || form.status === 'saving'}
        >
          Save Draft
        </Button>

        <AutosaveIndicator status={form.status} />

        <div className="ml-auto flex items-center gap-2">
          {!isLast && (
            <Button
              variant="primary"
              onClick={() => goToStep(Math.min(STEPS.length - 1, step + 1))}
            >
              Continue
            </Button>
          )}
          {isLast && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit || submitForReview.isPending}
            >
              {submitForReview.isPending ? 'Submitting…' : 'Submit for review'}
            </Button>
          )}
        </div>
      </div>

      {form.status === 'error' && form.error && (
        <p
          className="text-xs text-rose-600 dark:text-rose-400 mt-2 text-right"
          role="alert"
        >
          {form.error.message} — try Save Draft again, or keep editing and
          we'll retry on the next change.
        </p>
      )}

      {!canSubmit && isLast && (
        <p className="text-xs text-ink-500 dark:text-ink-400 mt-2 text-right">
          Add a full name and a short bio before submitting.
        </p>
      )}
      {submitError && (
        <p
          className="text-xs text-rose-600 dark:text-rose-400 mt-2 text-right"
          role="alert"
        >
          {submitError}
        </p>
      )}
    </div>
  );
}
